import { useCallback, useEffect, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import {
  buildSpotPnlFallbackAnalysis,
  normalizeSpotPnlAnalysisData,
  normalizeSpotPnlDetailsData,
  normalizeSpotPnlDateRange,
  SPOT_PNL_DETAILS_LIMIT_DEFAULT,
  SPOT_PNL_PERIOD_DEFAULT,
  spotPnlPeriodRange,
  paginateSpotPnlSeries,
} from "../../../api/services/spotPnlQuery";

/**
 * Primary: GET /spot/v1/me/pnl/analysis
 * Details: GET /spot/v1/me/pnl/analysis/details
 * Fallback: GET /spot/v1/me/pnl (trade PnL) when analysis endpoint fails
 */
export default function useSpotPnlAnalysis({ token, enabled = true } = {}) {
  const initialRange = spotPnlPeriodRange(SPOT_PNL_PERIOD_DEFAULT);
  const [period, setPeriod] = useState(SPOT_PNL_PERIOD_DEFAULT);
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [analysis, setAnalysis] = useState(null);
  const [detailsRows, setDetailsRows] = useState([]);
  const [detailsPagination, setDetailsPagination] = useState({ page: 1, total_pages: 1 });
  const [detailsPage, setDetailsPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  const applyPeriod = useCallback((nextPeriod) => {
    const range = spotPnlPeriodRange(nextPeriod);
    setPeriod(nextPeriod);
    setDateFrom(range.from);
    setDateTo(range.to);
    setDetailsPage(1);
  }, []);

  const setCustomRange = useCallback((from, to) => {
    setPeriod(null);
    setDetailsPage(1);
    if (from != null) setDateFrom(from);
    if (to != null) setDateTo(to);
  }, []);

  useEffect(() => {
    if (!dateFrom || !dateTo || dateFrom <= dateTo) return;
    setDateFrom(dateTo);
    setDateTo(dateFrom);
  }, [dateFrom, dateTo]);

  const queryParams = useCallback(() => {
    const { from, to } = normalizeSpotPnlDateRange(dateFrom, dateTo);
    return {
      from,
      to,
      period: from && to ? null : period,
    };
  }, [dateFrom, dateTo, period]);

  const fetchAnalysis = useCallback(async () => {
    if (!token || !enabled) return;
    const params = queryParams();
    setLoading(true);
    setError(null);
    setUsingFallback(false);

    try {
      const res = await AuthService.spotPnlAnalysis({
        ...params,
        include_allocation: true,
      });

      if (res?.success && res.data) {
        const normalized = normalizeSpotPnlAnalysisData(res.data);
        setAnalysis(normalized);
        if (normalized?.from) setDateFrom(normalized.from);
        if (normalized?.to) setDateTo(normalized.to);
        return;
      }

      const portfolioRes = await AuthService.estimatedPortfolio("spot");
      const portfolioData = portfolioRes?.success ? portfolioRes.data : null;

      const [p24, p7, p30] = await Promise.all([
        AuthService.spotMePnl({ period: "24h" }),
        AuthService.spotMePnl({ period: "7d" }),
        AuthService.spotMePnl({ period: "30d" }),
      ]);

      const fallback = buildSpotPnlFallbackAnalysis({
        pnl24h: p24?.success ? p24.data : null,
        pnl7d: p7?.success ? p7.data : null,
        pnl30d: p30?.success ? p30.data : null,
        portfolio: portfolioData,
      });

      setAnalysis(fallback);
      setUsingFallback(true);
      if (!fallback) {
        setError(res?.message || "Failed to load spot PnL analysis.");
      }
    } catch {
      setAnalysis(null);
      setError("Failed to load spot PnL analysis.");
    } finally {
      setLoading(false);
    }
  }, [token, enabled, queryParams]);

  const fetchDetails = useCallback(async () => {
    if (!token || !enabled || !analysis) return;

    if (usingFallback) {
      const slice = paginateSpotPnlSeries(
        analysis.series,
        detailsPage,
        SPOT_PNL_DETAILS_LIMIT_DEFAULT
      );
      setDetailsRows(slice.rows);
      setDetailsPagination(slice.pagination);
      return;
    }

    const params = queryParams();
    setDetailsLoading(true);
    try {
      const res = await AuthService.spotPnlAnalysisDetails({
        ...params,
        page: detailsPage,
        limit: SPOT_PNL_DETAILS_LIMIT_DEFAULT,
      });

      if (res?.success) {
        const normalized = normalizeSpotPnlDetailsData(
          res.data,
          res.pagination ?? res.data?.pagination
        );
        setDetailsRows(normalized.rows);
        setDetailsPagination(normalized.pagination);
        return;
      }

      const slice = paginateSpotPnlSeries(
        analysis.series,
        detailsPage,
        SPOT_PNL_DETAILS_LIMIT_DEFAULT
      );
      setDetailsRows(slice.rows);
      setDetailsPagination(slice.pagination);
    } catch {
      const slice = paginateSpotPnlSeries(
        analysis.series,
        detailsPage,
        SPOT_PNL_DETAILS_LIMIT_DEFAULT
      );
      setDetailsRows(slice.rows);
      setDetailsPagination(slice.pagination);
    } finally {
      setDetailsLoading(false);
    }
  }, [token, enabled, analysis, usingFallback, detailsPage, queryParams]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  return {
    period,
    dateFrom,
    dateTo,
    analysis,
    detailsRows,
    detailsPagination,
    detailsPage,
    loading,
    detailsLoading,
    error,
    usingFallback,
    applyPeriod,
    setCustomRange,
    setDetailsPage,
    refetch: fetchAnalysis,
  };
}
